import React from "react";
import { Zap, Wrench, Building, MapPin, Mail } from "lucide-react";
import SiteShell from "../../components/SiteShell";
import SEO from "../../components/SEO";
import { DeptHero, DeptSection, PersonCard, CardGrid } from "./_shared";

/**
 * /team/solar-power — Solar Power Plant deployment team.
 * Ported from client Vite/TSX source; branding scrubbed. The public
 * contact email is a placeholder ("hello@greengold.energy") pending the
 * client confirming the definitive address.
 */

const electricalTeam = [
  { name: "Viswanathan K", role: "Senior Project Coordinator" },
  { name: "Vignesh", role: "PV Design / Draft Person" },
  { name: "S. Manikandan", role: "Project Engineer PV" },
  { name: "Er. Prabhakaran", role: "Electrical Trade Executor / Estimator" },
  { name: "L. Venkatesh", role: "Project Engineer PV" },
  { name: "Justin Charles", role: "Electrical Engineer" },
  { name: "Sarvanan", role: "Electrical Graduate Engineer" },
  { name: "Salman", role: "PV Engineer / Assembler" },
  { name: "Santhoshkumar C", role: "PV Engineer / Assembler" },
  { name: "M. Moganraj", role: "Field Electrical Assistant" },
  { name: "T. Nandakumar", role: "Field Electrical Assistant" },
  { name: "P. Ruban Raj", role: "LV / HV Panel Fitter Coordinator" },
  { name: "E. Rajkumar", role: "Electrical Fitter Coordinator" },
  { name: "N. Vaasanth", role: "Transmission Lay Coordinator" },
  { name: "Y. Enayathali", role: "Switchyard Electrical Coordinator" },
  { name: "R. Paramaguru", role: "Electrician" },
  { name: "Manivannan", role: "DC Cabling Coordinator" },
  { name: "Ravichandaran", role: "Electrician" },
];

const mechanicalTeam = [
  { name: "Mohan Raj", role: "Structural Engineer Mechanical" },
  { name: "J. Ramesh", role: "Estimator Mechanical" },
  { name: "M. Arun", role: "Mechanical Supervisor" },
  { name: "Rajiv Ghandhi", role: "MMS Coordinator" },
  { name: "Siva Suriyan", role: "MMS Coordinator" },
  { name: "Vetri Selvam", role: "MMS Coordinator" },
  { name: "Silambarasan", role: "Fabricator Coordinator" },
  { name: "Arul", role: "Fabricator Coordinator" },
  { name: "Muthu", role: "Welder Coordinator" },
  { name: "K. Surya", role: "Welder Coordinator" },
];

const civilTeam = [
  { name: "Senthil", role: "Senior Civil Engineer" },
  { name: "Chezhiyan", role: "Senior Civil Engineer / Estimator" },
  { name: "Selvendharan", role: "Site Civil Supervisor" },
  { name: "Navarasan", role: "Site Civil Engineer" },
  { name: "Sarvana Kumar", role: "Site Civil Engineer" },
  { name: "Kumar", role: "Civil Equipment Handler" },
  { name: "Raj Kumar", role: "Backhoe Operator / Fork Lifter" },
  { name: "Madhialagan", role: "Crane Operator" },
];

const landTeam = [
  { name: "Balasubramaniam", role: "Land Procurement" },
  { name: "Rajan", role: "TNEB Approvals" },
];

const offices = [
  {
    city: "Race Course Office, Coimbatore",
    address:
      "8 Race Course Rd, Thomas Park, Race Course, Coimbatore, Tamil Nadu 641018",
  },
  {
    city: "Jaipur Office",
    address: "31 Palm Courts, Jagatpura, Jaipur, Rajasthan 302025",
  },
];

export default function SolarPower() {
  return (
    <SiteShell>
      <SEO
        title="Solar Power Plant Team — Green Gold Energy"
        description="Green Gold Energy's Solar Power Plant team across Electrical, Mechanical, Civil and Land Acquisition & Substation departments."
      />
      <DeptHero
        kicker="Solar Power Plant"
        title="Utility-scale solar EPC and O&M"
        sub="Utility-scale solar, floating solar, and hybrid Solar+BESS across 31 countries — the execution muscle behind the portfolio. Electrical, Mechanical, Civil, and Land Acquisition teams that carry a 500+ MWp track record and every TNEB / TANGEDCO / CEIG approval that comes with it."
        Icon={Zap}
      />

      <DeptSection
        Icon={Zap}
        title="Electrical Team"
        description="Responsible for PV design, switchyard coordination, electrical fitting, panel layout planning, and cabling."
      >
        <CardGrid minCol={260}>
          {electricalTeam.map((m) => (
            <PersonCard key={m.name} member={m} variant="compact" />
          ))}
        </CardGrid>
      </DeptSection>

      <DeptSection
        Icon={Wrench}
        title="Mechanical Team"
        description="Ensures the structural integrity and design of Mounting Magnetic Structures (MMS) along with fabrication and precise welding coordination."
      >
        <CardGrid minCol={260}>
          {mechanicalTeam.map((m) => (
            <PersonCard key={m.name} member={m} variant="compact" />
          ))}
        </CardGrid>
      </DeptSection>

      <DeptSection
        Icon={Building}
        title="Civil Team"
        description="Manages foundations, site evaluation, and heavy machinery operations essential for constructing sustainable solar fields."
      >
        <CardGrid minCol={260}>
          {civilTeam.map((m) => (
            <PersonCard key={m.name} member={m} variant="compact" />
          ))}
        </CardGrid>
      </DeptSection>

      <DeptSection
        Icon={MapPin}
        title="Land Acquisition & Substation Team"
        description="Secures project land boundaries and liaises with state electricity boards for power grid synchronization."
      >
        <CardGrid minCol={260}>
          {landTeam.map((m) => (
            <PersonCard key={m.name} member={m} variant="compact" />
          ))}
        </CardGrid>
      </DeptSection>

      <DeptSection Icon={MapPin} title="Corporate Presence">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: 16,
          }}
        >
          {offices.map((office) => (
            <div
              key={office.city}
              className="gg-glass"
              style={{ padding: 22 }}
              data-testid={`office-${office.city.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 999,
                  background: "rgba(244,169,31,0.14)",
                  border: "1px solid rgba(244,169,31,0.32)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--gold-soft)",
                  marginBottom: 14,
                }}
              >
                <MapPin size={20} strokeWidth={1.7} />
              </div>
              <h3
                style={{
                  fontFamily: "Fraunces, Georgia, serif",
                  fontSize: 18,
                  color: "var(--paper)",
                  marginBottom: 6,
                }}
              >
                {office.city}
              </h3>
              <p
                style={{
                  color: "var(--text-dim)",
                  fontSize: 14,
                  lineHeight: 1.6,
                }}
              >
                {office.address}
              </p>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 32, textAlign: "center" }}>
          {/* TBD — awaiting confirmed contact email */}
          <a
            href="mailto:hello@greengold.energy"
            data-testid="solar-power-contact-mail"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              padding: "12px 22px",
              borderRadius: 999,
              border: "1px solid rgba(244,169,31,0.4)",
              background: "rgba(244,169,31,0.12)",
              color: "var(--gold-soft)",
              textDecoration: "none",
              fontSize: 14,
              letterSpacing: "0.02em",
              transition: "background 220ms, border-color 220ms",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(244,169,31,0.2)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(244,169,31,0.12)";
            }}
          >
            <Mail size={16} strokeWidth={1.8} />
            hello@greengold.energy
          </a>
        </div>
      </DeptSection>

      <div style={{ height: 60 }} />
    </SiteShell>
  );
}
